import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { 
  Zap, 
  ArrowLeft, 
  CheckCircle,
  Loader2,
  MapPin,
  Calculator,
  Package,
  Building2,
  ChevronsUpDown,
  Check,
  AlertCircle,
  Bookmark,
  ClipboardList
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import Navigation from "@/components/Navigation";
import { dualPriceText } from "@/components/DualPrice";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function QuickReceivePage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // Data from scan
  const [invoiceInfo, setInvoiceInfo] = useState(null);
  const [items, setItems] = useState([]);
  
  // Form state
  const [suppliers, setSuppliers] = useState([]);
  const [locations, setLocations] = useState([]);
  const [selectedSupplier, setSelectedSupplier] = useState("");
  const [exchangeRate, setExchangeRate] = useState(11.5);
  const [defaultLocation, setDefaultLocation] = useState("MOTTAGEN ORDER");
  const [freightCost, setFreightCost] = useState("");
  const [freightDistribution, setFreightDistribution] = useState("per_item");
  const [notes, setNotes] = useState("");
  const [openLocationPopover, setOpenLocationPopover] = useState({});
  
  // Reservations dialog
  const [showReservationsDialog, setShowReservationsDialog] = useState(false);
  const [convertedReservations, setConvertedReservations] = useState([]);

  useEffect(() => {
    loadScannedData();
    fetchData();
  }, []);

  const loadScannedData = () => {
    try {
      const storedItems = sessionStorage.getItem('quickReceiveItems');
      const storedInfo = sessionStorage.getItem('quickReceiveInfo');
      
      if (!storedItems) {
        toast.error("Inga skannade produkter hittades. Gå tillbaka och skanna en faktura.");
        setTimeout(() => navigate('/purchasing/scan'), 1500);
        return;
      }
      
      const parsedItems = JSON.parse(storedItems);
      if (!parsedItems || parsedItems.length === 0) {
        toast.error("Produktlistan är tom. Gå tillbaka och skanna en faktura.");
        setTimeout(() => navigate('/purchasing/scan'), 1500);
        return;
      }
      
      // Add location and receive quantity to each item
      const itemsWithLocation = parsedItems.map(item => ({
        ...item,
        location: "MOTTAGEN ORDER",
        receive_quantity: item.quantity || 1
      }));
      setItems(itemsWithLocation);
      
      if (storedInfo) {
        const parsedInfo = JSON.parse(storedInfo);
        setInvoiceInfo(parsedInfo);
        
        // Set notes with invoice reference
        if (parsedInfo.invoice_number) {
          setNotes(`Faktura: ${parsedInfo.invoice_number}${parsedInfo.invoice_date ? ` (${parsedInfo.invoice_date})` : ''}`);
        }
      }
      
      // Don't clear sessionStorage here - clear it after successful receive
      
    } catch (error) {
      console.error('Error loading scanned data:', error);
      toast.error("Kunde inte ladda skannade produkter");
      navigate('/purchasing/scan');
    }
  };

  const fetchData = async () => {
    try {
      const [suppliersRes, locationsRes] = await Promise.all([
        axios.get(`${API}/purchasing/suppliers`),
        axios.get(`${API}/locations`)
      ]);
      
      setSuppliers(suppliersRes.data);
      try {
        const rateRes = await axios.get(`${API}/purchasing/exchange-rate`);
        setExchangeRate(rateRes.data?.rate || 11.5);
      } catch { /* keep default */ }

      // Auto-select supplier from scan match
      try {
        const storedInfo = sessionStorage.getItem('quickReceiveInfo');
        if (storedInfo) {
          const info = JSON.parse(storedInfo);
          if (info.matched_supplier?.id) {
            setSelectedSupplier(info.matched_supplier.id);
          }
        }
      } catch { /* ignore */ }
      
      // Handle locations - could be strings or objects
      const locs = locationsRes.data.map(loc => 
        typeof loc === 'string' ? { name: loc, id: loc } : loc
      );
      // Sort: indoor first
      const sorted = locs.sort((a, b) => {
        const aOutdoor = a.name.toLowerCase().includes('utomhus') || a.name.startsWith('U');
        const bOutdoor = b.name.toLowerCase().includes('utomhus') || b.name.startsWith('U');
        if (aOutdoor && !bOutdoor) return 1;
        if (!aOutdoor && bOutdoor) return -1;
        return a.name.localeCompare(b.name, 'sv');
      });
      setLocations(sorted);
      
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  const updateItemQuantity = (index, value) => {
    const qty = parseInt(value) || 0;
    setItems(prev => prev.map((item, i) => {
      if (i === index) {
        return { ...item, receive_quantity: Math.max(0, qty) };
      }
      return item;
    }));
  };

  const updateItemLocation = (index, location) => {
    setItems(prev => prev.map((item, i) => {
      if (i === index) {
        return { ...item, location };
      }
      return item;
    }));
  };

  const applyDefaultLocation = () => {
    setItems(prev => prev.map(item => ({
      ...item,
      location: defaultLocation
    })));
    toast.success(`Lagerplats "${defaultLocation}" applicerad på alla produkter`);
  };

  const handleSubmit = async () => {
    const itemsToReceive = items.filter(i => i.receive_quantity > 0);
    
    if (itemsToReceive.length === 0) {
      toast.error("Ange antal för minst en produkt");
      return;
    }
    
    // Check all items have locations
    const missingLocation = itemsToReceive.find(i => !i.location?.trim());
    if (missingLocation) {
      toast.error(`Ange lagerplats för ${missingLocation.produktnamn}`);
      return;
    }
    
    setSaving(true);
    
    try {
      // Use batch endpoint to receive all items at once and create a delivery receipt
      const batchPayload = {
        items: itemsToReceive.map(item => ({
          ean: item.ean || "",
          artikelnummer: item.artikelnummer || item.system_artikelnummer || "",
          produktnamn: item.produktnamn || item.system_produktnamn || "",
          quantity: item.receive_quantity,
          location: item.location,
          unit_price: item.unit_price || 0,
          freight_per_unit: 0, // Will be calculated by backend
          supplier_id: (selectedSupplier && selectedSupplier !== "none") ? selectedSupplier : null,
          order_id: item.order_info?.order_id || null
        })),
        supplier_id: (selectedSupplier && selectedSupplier !== "none") ? selectedSupplier : null,
        freight_cost: parseFloat(freightCost) || 0,
        freight_distribution: freightDistribution,
        notes: notes || `Snabbmottagning ${new Date().toISOString()}`
      };
      
      const response = await axios.post(`${API}/purchasing/quick-receive-batch`, batchPayload);
      
      // Clear sessionStorage after successful receive
      sessionStorage.removeItem('quickReceiveItems');
      sessionStorage.removeItem('quickReceiveInfo');
      
      toast.success(`${itemsToReceive.length} produkter mottagna till lager`);
      navigate('/purchasing/orders/received');
      
    } catch (error) {
      console.error("Error receiving items:", error);
      const errorDetail = error.response?.data?.detail;
      let errorMessage = "Kunde inte registrera mottagning";
      if (typeof errorDetail === 'string') {
        errorMessage = errorDetail;
      } else if (Array.isArray(errorDetail)) {
        errorMessage = errorDetail.map(e => e.msg || JSON.stringify(e)).join(', ');
      } else if (errorDetail && typeof errorDetail === 'object') {
        errorMessage = errorDetail.msg || JSON.stringify(errorDetail);
      }
      toast.error(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  const totalToReceive = items.reduce((sum, i) => sum + (i.receive_quantity || 0), 0);
  const totalValue = items.reduce((sum, i) => sum + ((i.unit_price || 0) * (i.receive_quantity || 0)), 0);
  const freightPerItem = freightCost && totalToReceive > 0 
    ? (parseFloat(freightCost) / totalToReceive).toFixed(2)
    : 0;

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background pb-20 md:pb-0">
      <Navigation />
      
      {/* Header */}
      <header className="border-b border-border bg-card">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center gap-3">
            <Link to="/purchasing/scan">
              <Button variant="ghost" size="icon">
                <ArrowLeft className="h-5 w-5" />
              </Button>
            </Link>
            <div>
              <div className="flex items-center gap-2">
                <Zap className="h-5 w-5 text-yellow-500" />
                <h1 className="font-heading text-xl font-bold uppercase tracking-tight">
                  Snabbmottagning
                </h1>
              </div>
              <p className="text-sm text-muted-foreground">
                Ta emot produkter direkt till lager utan inköpsorder
              </p>
            </div>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 py-6 max-w-4xl space-y-6">
        {/* Invoice Info */}
        {invoiceInfo && (invoiceInfo.supplier_info || invoiceInfo.invoice_number) && (
          <Card className="bg-yellow-500/10 border-yellow-500/30">
            <CardContent className="p-4 flex items-center gap-3">
              <Zap className="h-5 w-5 text-yellow-500" />
              <div>
                <p className="font-medium">Produkter från skanning</p>
                <p className="text-sm text-muted-foreground">
                  {invoiceInfo.supplier_info?.company_name && `Leverantör: ${invoiceInfo.supplier_info.company_name}`}
                  {invoiceInfo.invoice_number && ` | Faktura: ${invoiceInfo.invoice_number}`}
                </p>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Supplier Selection (Optional) */}
        <Card>
          <CardHeader>
            <CardTitle className="font-heading uppercase text-base">Leverantör (valfritt)</CardTitle>
            <CardDescription>
              Koppla mottagningen till en leverantör för spårbarhet
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Select value={selectedSupplier} onValueChange={setSelectedSupplier}>
              <SelectTrigger>
                <SelectValue placeholder="Välj leverantör (valfritt)..." />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Ingen leverantör</SelectItem>
                {suppliers.map(s => (
                  <SelectItem key={s.id} value={s.id}>{s.company_name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </CardContent>
        </Card>

        {/* Default Location */}
        <Card>
          <CardContent className="p-4">
            <div className="flex items-end gap-4">
              <div className="flex-1 space-y-2">
                <Label>Standardlagerplats för alla produkter</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      role="combobox"
                      className="w-full justify-between font-normal"
                    >
                      {defaultLocation || "Välj lagerplats..."}
                      <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-full p-0" align="start">
                    <Command>
                      <CommandInput placeholder="Sök lagerplats..." />
                      <CommandList>
                        <CommandEmpty>Ingen lagerplats hittades</CommandEmpty>
                        <CommandGroup>
                          <CommandItem
                            value="MOTTAGEN ORDER"
                            onSelect={() => setDefaultLocation("MOTTAGEN ORDER")}
                          >
                            <Check className={cn("mr-2 h-4 w-4", defaultLocation === "MOTTAGEN ORDER" ? "opacity-100" : "opacity-0")} />
                            MOTTAGEN ORDER
                          </CommandItem>
                          {locations.map((loc) => (
                            <CommandItem
                              key={loc.id || loc.name}
                              value={loc.name}
                              onSelect={() => setDefaultLocation(loc.name)}
                            >
                              <Check className={cn("mr-2 h-4 w-4", defaultLocation === loc.name ? "opacity-100" : "opacity-0")} />
                              {loc.name}
                            </CommandItem>
                          ))}
                        </CommandGroup>
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
              </div>
              <Button variant="outline" onClick={applyDefaultLocation}>
                <MapPin className="h-4 w-4 mr-2" />
                Applicera på alla
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Products */}
        <Card>
          <CardHeader>
            <CardTitle className="font-heading uppercase">Produkter att ta emot ({items.length})</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {items.map((item, index) => (
              <Card key={index} className="border-border">
                <CardContent className="p-3">
                  <div className="flex flex-col gap-3">
                    <div className="flex items-start justify-between">
                      <div>
                        <p className="font-medium">{item.produktnamn || 'Okänd produkt'}</p>
                        <p className="text-xs text-muted-foreground font-mono">{item.ean || 'Inget EAN'}</p>
                        {item.artikelnummer && (
                          <p className="text-xs text-muted-foreground">Art.nr: {item.artikelnummer}</p>
                        )}
                      </div>
                      {item.unit_price > 0 && (
                        <Badge variant="outline">
                          {dualPriceText(item.unit_price, item.currency || 'SEK', exchangeRate)}/st
                        </Badge>
                      )}
                    </div>
                    
                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-1">
                        <Label className="text-xs">Antal att ta emot</Label>
                        <Input
                          type="number"
                          min="0"
                          value={item.receive_quantity}
                          onChange={(e) => updateItemQuantity(index, e.target.value)}
                        />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs">Lagerplats</Label>
                        <Popover
                          open={openLocationPopover[index]}
                          onOpenChange={(open) => setOpenLocationPopover(prev => ({ ...prev, [index]: open }))}
                        >
                          <PopoverTrigger asChild>
                            <Button
                              variant="outline"
                              role="combobox"
                              className="w-full justify-between font-normal text-sm h-9"
                            >
                              {item.location || "Välj plats..."}
                              <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent className="w-[200px] p-0" align="start">
                            <Command>
                              <CommandInput placeholder="Sök..." className="h-8" />
                              <CommandList>
                                <CommandEmpty>Ingen plats hittades</CommandEmpty>
                                <CommandGroup>
                                  <CommandItem
                                    value="MOTTAGEN ORDER"
                                    onSelect={() => {
                                      updateItemLocation(index, "MOTTAGEN ORDER");
                                      setOpenLocationPopover(prev => ({ ...prev, [index]: false }));
                                    }}
                                  >
                                    <Check className={cn("mr-2 h-4 w-4", item.location === "MOTTAGEN ORDER" ? "opacity-100" : "opacity-0")} />
                                    MOTTAGEN ORDER
                                  </CommandItem>
                                  {locations.map((loc) => (
                                    <CommandItem
                                      key={loc.id || loc.name}
                                      value={loc.name}
                                      onSelect={() => {
                                        updateItemLocation(index, loc.name);
                                        setOpenLocationPopover(prev => ({ ...prev, [index]: false }));
                                      }}
                                    >
                                      <Check className={cn("mr-2 h-4 w-4", item.location === loc.name ? "opacity-100" : "opacity-0")} />
                                      {loc.name}
                                    </CommandItem>
                                  ))}
                                </CommandGroup>
                              </CommandList>
                            </Command>
                          </PopoverContent>
                        </Popover>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </CardContent>
        </Card>

        {/* Freight Cost */}
        <Card>
          <CardHeader>
            <CardTitle className="font-heading uppercase flex items-center gap-2">
              <Calculator className="h-5 w-5" />
              Fraktkostnad (valfritt)
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Total fraktkostnad (SEK)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={freightCost}
                  onChange={(e) => setFreightCost(e.target.value)}
                  placeholder="0.00"
                />
              </div>
              <div className="space-y-2">
                <Label>Fördelning</Label>
                <Select value={freightDistribution} onValueChange={setFreightDistribution}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="per_item">Per styck (lika fördelning)</SelectItem>
                    <SelectItem value="proportional">Proportionellt mot värde</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            {freightCost && totalToReceive > 0 && (
              <div className="p-3 bg-muted rounded-md text-sm">
                <p>
                  {freightDistribution === "per_item" 
                    ? `Frakt per enhet: ${freightPerItem} SEK`
                    : "Frakten fördelas proportionellt mot produkternas värde"
                  }
                </p>
              </div>
            )}
            
            <div className="space-y-2">
              <Label>Anteckningar / Referens</Label>
              <Textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="T.ex. fakturanummer, leveransnoteringar..."
                rows={2}
              />
            </div>
          </CardContent>
        </Card>

        {/* Summary & Submit */}
        <Card className="bg-green-500/10 border-green-500/30">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Sammanfattning</p>
                <p className="text-lg font-bold">
                  {totalToReceive} produkter att ta emot
                </p>
                {totalValue > 0 && (
                  <p className="text-sm">
                    Värde: {totalValue.toFixed(2)} SEK
                    {freightCost && ` + ${freightCost} SEK frakt`}
                  </p>
                )}
              </div>
              <Button 
                onClick={handleSubmit} 
                disabled={saving || totalToReceive === 0}
                className="gap-2 bg-green-600 hover:bg-green-700"
                size="lg"
                data-testid="quick-receive-btn"
              >
                {saving ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Registrerar...
                  </>
                ) : (
                  <>
                    <CheckCircle className="h-4 w-4" />
                    Ta emot till lager
                  </>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
